import { clone, flow, getSnapshot, types, runInAction } from 'mobx-state-tree';
import { Interaction } from './Meeting';
import { ApiStore } from './ApiStore';
import { NullVideo, NullVideoChunk, Video, VideoChunck } from './VideoPresentation';
import * as Sentry from '@sentry/react';

// Retry an async operation on transient failure with exponential backoff.
// `shouldRetry` gates which errors are worth retrying (default: all of them).
const retryWithBackoff = async (operation, { attempts = 3, baseDelayMs = 500, shouldRetry = () => true } = {}) => {
    let lastError;
    for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;
            if (attempt === attempts || !shouldRetry(error)) break;
            await new Promise(resolve => setTimeout(resolve, baseDelayMs * Math.pow(2, attempt - 1)));
        }
    }
    throw lastError;
};

// Upload a single multipart part via its presigned PUT URL. Resolves with the
// cleaned ETag; rejects on network error, non-200, or a missing ETag header so
// the caller can retry. Re-PUTting the same part number is idempotent on S3.
const putPart = (uploadUrl, blob, partNumber) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.onerror = () => reject(new Error('XHR network error'));
    xhr.onreadystatechange = () => {
        if (xhr.readyState !== XMLHttpRequest.DONE) return;
        if (xhr.status === 200) {
            const rawETag = xhr.getResponseHeader('ETag');
            if (!rawETag) {
                reject(new Error(`Upload part ${partNumber} returned no ETag header`));
                return;
            }
            resolve(rawETag.replace(/"/g, ''));
        } else {
            const error = new Error(`Upload part ${partNumber} failed with status ${xhr.status}`);
            error.status = xhr.status;
            reject(error);
        }
    };
    xhr.send(blob);
});

// Retry part PUTs on network errors (no status) and 5xx; a deterministic 4xx
// (e.g. bad signature) won't succeed on re-send, so don't re-upload the blob for it.
const isRetriablePutError = error => error.status === undefined || error.status >= 500;

const videoPresentation = types.model('VideoPresentationStore', {
    videoData: types.optional(Video, getSnapshot(NullVideo)),
    videoEtagData: types.optional(types.array(types.frozen()), []),
    videoChunck: types.optional(VideoChunck, getSnapshot(NullVideoChunk)),
    loading: types.optional(types.boolean, false),
    error: types.optional(types.string, ''),
    recordingType: types.optional(types.string, ''),
    uploadProgress: types.optional(types.string, 'Video Uploading...'),
    participants: types.optional(types.array(types.frozen()), []),
    interactions: types.optional(types.array(Interaction), []),  // Store MST model instances
    respondedStartTimes: types.optional(types.array(types.number), [])  // Track which interactions the user clicked
});

export const VideoPresentationStore = types.compose(videoPresentation, ApiStore)
    .views(self => ({
        get getAppEtags() {
            return self.videoEtagData;
        }
    }
    ))
    .actions((self) => ({
        reset() {
            // Clear all state when switching between meeting types
            self.videoData = getSnapshot(NullVideo);
            self.videoEtagData.clear();
            self.videoChunck = getSnapshot(NullVideoChunk);
            self.loading = false;
            self.error = '';
            self.uploadProgress = 'Video Uploading...';
            self.participants.clear();
            self.interactions.clear();
            self.respondedStartTimes.clear();
            console.log('VideoPresentationStore: State cleared for new recording session');
        },
        setRecordingType(recordingType) {
            self.recordingType = recordingType;
        },
        setParticipants(participants) {
            self.participants = participants;
        },
        setInteractions(interactions) {
            // Now expects safe interaction snapshots (plain objects)
            self.interactions = interactions || [];
        },
        markInteractionResponded(startTime) {
            if (!self.respondedStartTimes.includes(startTime)) {
                self.respondedStartTimes.push(startTime);
            }
        },
        uploadInitialChunk: flow(function* uploadInitialChunk(data) {
            self.videoEtagData = [];
            const body = {
                userId: data.userId,
                ContentType: data.ContentType,
                numberOfTries: data.numberOfTries,
                companyId: data.companyId,
                interactions: getInteractionDtos(self.interactions),
                interactionId: data.interactionId  // Include interaction ID for Murphy question tracking
            };
            self.loading = true;
            self.error = '';
            try {
                const res = clone(NullVideo);
                yield self.postApi(`/companies/${data.companyId}/initialize-upload/${data.key}`, body, res);
                self.videoData = res;
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error fetching data:', error);
                self.error = error.message;
            } finally {
                self.loading = false;
            }
        }),
        uploadChunk: flow(function* uploadChunk(data, blob, isFinal = false) {
            // Validate that we have the necessary video data before proceeding
            if (!self.videoData || !self.videoData.key) {
                console.error('uploadChunk called without proper video data initialization. Call uploadInitialChunk first.');
                self.error = 'Video upload not initialized. Please start recording again.';
                return;
            }

            const body = { userId: data.userId, ContentType: data.ContentType, PartNumber: data.partNumber, uploadId: self.videoData.uploadId };
            self.loading = true;
            self.error = '';
            try {
                const res = clone(NullVideoChunk);
                const keyWithoutExtension = self.videoData.key.split('/')[1].split('.')[0];
                // Retry transient network failures on the per-part init call too — the backend
                // is idempotent per part (re-calling just issues a fresh presigned URL).
                yield retryWithBackoff(
                    () => self.postApi(`/companies/${data.companyId}/initialize-upload/${keyWithoutExtension}`, body, res),
                    { shouldRetry: error => !error.response }
                );
                self.videoChunck = res;
                if (res.uploadUrl) {
                    // Upload the part, retrying transient network failures with backoff so a
                    // single flaky PUT doesn't fail the whole recording (common on large
                    // interactive uploads with many parts). The ETag is recorded only on the
                    // successful attempt, so a retry never double-counts a part.
                    const ETag = yield retryWithBackoff(
                        () => putPart(self.videoChunck.uploadUrl, blob, data.partNumber),
                        { shouldRetry: isRetriablePutError }
                    );
                    self.addVideoEtagData({ PartNumber: data.partNumber, ETag });
                    if (isFinal) {
                        yield self.finalizeUpload(data);
                    }
                }
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error uploading chunk:', error);
                self.error = error.message;
            } finally {
                self.loading = false;
            }
        }),
        addVideoEtagData(item) {
            self.videoEtagData.push(item);
        },
        finalizeUpload: flow(function* finalizeUpload(data) {
            const body = {
                uploadId: self.videoData.uploadId,
                key: self.videoChunck.key,
                parts: self.videoEtagData,
                respondedStartTimes: self.respondedStartTimes.slice()  // Which interactions the user clicked
            };
            self.loading = true;
            self.error = '';
            if (Array.isArray(self.videoEtagData) && self.videoEtagData.length > 0) {
                try {
                    // Retry only transient network/timeout failures (no HTTP response). A finalize
                    // request that reached the server completes the upload there even if the client
                    // never gets the response, so re-issuing it after an HTTP error would hit an
                    // already-completed multipart (NoSuchUpload) — don't retry those.
                    yield retryWithBackoff(
                        () => self.postApi(`/companies/${data.companyId}/finalize-upload`, body),
                        { shouldRetry: error => !error.response }
                    );
                    self.setUploadProgress('Video upload complete. Sending to Murphy for Grading');
                } catch (error) {
                    Sentry.captureException(error);
                    console.error('Error fetching data:', error);
                    self.error = error.message;
                } finally {
                    self.loading = false;
                }
            }
        }),
        setUploadProgress(progress) {
            self.uploadProgress = progress;
        },
        getQuestionAudio: flow(function* getQuestionAudio(questionTitle, voiceTag, format) {
            self.error = '';
            try {
                console.log(`Getting audio for question: "${questionTitle}" with voice: ${voiceTag}`);
                const response = yield self.getApi(`/presigned-url?assetType=murphy-audio&key=audio/questions/${questionTitle}_${voiceTag}.${format}`);

                const responseJSON = JSON.parse(response.body);

                if (!responseJSON.url) {
                    console.log(responseJSON);
                    throw new Error(`Failed to get signed URL for audio file!`);
                }

                return responseJSON.url; // Changed from signedUrl to url to match Lambda response
            } catch (error) {
                console.error('Error fetching audio URL:', error);
                self.error = error.message;
                return null;
            }
        })
    }));

const getInteractionDtos = (interactionItems) => {
    return interactionItems?.map(item => ({
        text: item.text,
        // title: item.title, // Uncomment if you have a title property
        participant: item.participantName
    }));
};

